import { Box, Divider, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useRecoilState } from 'recoil';
import ResetButton from '~/components/Buttons/ResetButton';
import { Slider } from '~/components/Slider';
import { DEFAULT_DOSE_OPACITY_VALUE } from '~/engine/data/dose/DoseDistribution';
import {
    currentDoseState,
    doseOpacityState,
} from '~/state/atoms/dose/doseAtoms';
import { environmentState } from '~/state/atoms/engineAtoms';

const DOSE_OPACITY_MIN_VALUE = 0;
const DOSE_OPACITY_MAX_VALUE = 1;
const DOSE_OPACITY_STEP_VALUE = 0.1;

type DoseOpacityModifierProps = {
    showReset?: boolean;
};

export default function DoseOpacityModifier({
    showReset = true,
}: DoseOpacityModifierProps) {
    const [environment] = useRecoilState(environmentState);
    const [currentDose] = useRecoilState(currentDoseState);
    const [doseOpacity, setDoseOpacity] =
        useRecoilState<number>(doseOpacityState);

    const handleSliderChange = (newValue: number): void => {
        const percent = validateValue(newValue / 100);
        setDoseOpacity(percent);
        if (environment) {
            environment.updateDoseAlphaLevelOpacity(percent);
        }
    };

    const validateValue = (value: number): number => {
        return Math.min(
            Math.max(value, DOSE_OPACITY_MIN_VALUE),
            DOSE_OPACITY_MAX_VALUE
        );
    };

    const handleReset = () => {
        setDoseOpacity(DEFAULT_DOSE_OPACITY_VALUE);
        if (environment) {
            environment.updateDoseAlphaLevelOpacity(DEFAULT_DOSE_OPACITY_VALUE);
        }
    };

    return (
        <Stack
            display="flex"
            flexDirection="column"
            gap={1}
            data-testid="dose-opacity-modifier"
        >
            <Slider
                label={
                    <Box display={'flex'} alignItems={'center'}>
                        <Typography fontSize={13}>
                            <FormattedMessage id="doseOpacity" />
                        </Typography>
                        {showReset && (
                            <>
                                <Divider
                                    flexItem
                                    orientation="vertical"
                                    sx={{ mx: 1, my: 0.5 }}
                                />
                                <ResetButton
                                    onClick={handleReset}
                                    disabled={!currentDose}
                                />
                            </>
                        )}
                    </Box>
                }
                value={doseOpacity * 100}
                onChange={(v) => handleSliderChange(v)}
                min={DOSE_OPACITY_MIN_VALUE * 100}
                max={DOSE_OPACITY_MAX_VALUE * 100}
                step={DOSE_OPACITY_STEP_VALUE * 100}
                disabled={!currentDose}
                testId="dose-opacity-slider"
            />
        </Stack>
    );
}
